import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { fieldWorkKinds, type FieldWorkKindEntry } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { formatMoney } from '@/lib/money';

/**
 * Admin: cennik prac terenowych (V186) — rodzaje prac (regeneracja, inspekcja TV, diagnostyka…)
 * z jedną ceną netto za jednostkę. Osobno od cennika opracowań: te rodzaje wybiera się w rejestrze
 * prac terenowych, a kreator oferty wstawia ich ceny do kosztorysu.
 */
export function FieldWorkKindAdmin() {
  const [rows, setRows] = useState<FieldWorkKindEntry[] | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<FieldWorkKindEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    setErr(null);
    setRows(null);
    fieldWorkKinds.list(includeInactive).then(setRows).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [includeInactive]);

  const onDeactivate = async (k: FieldWorkKindEntry) => {
    if (!confirm(`Wyłączyć rodzaj „${k.name}”? Prace, które go mają, zostaną bez zmian; nowym nie da się go wybrać.`)) return;
    try {
      await fieldWorkKinds.delete(k.id);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Cennik prac terenowych</h1>
          <div className="text-ink-400 text-[13px]">
            Rodzaje prac na studniach z ceną netto za jednostkę. Wybiera się je w rejestrze prac terenowych,
            a w kreatorze oferty przycisk „Z cennika prac terenowych” wstawia je do kosztorysu.
          </div>
        </div>
        <div className="flex gap-2">
          <label className="text-[12px] text-ink-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)}/>
            Pokaż wyłączone
          </label>
          <button onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Nowy rodzaj
          </button>
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="card">
        <div className="card-body !p-0">
          {!rows && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {rows && rows.length === 0 && <div className="p-4 text-ink-400">Brak rodzajów. Dodaj pierwszy.</div>}
          {rows && rows.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Rodzaj pracy</th>
                  <th className="w-24">Jedn.</th>
                  <th className="w-36 text-right">Cena netto</th>
                  <th className="w-28">Cykliczna</th>
                  <th className="w-20">Sort</th>
                  <th className="w-24">Status</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(k => (
                  <tr key={k.id} className={k.active ? '' : 'opacity-50'}>
                    <td>
                      <div className="text-ink-900">{k.name}</div>
                      {k.description && <div className="text-[11px] text-ink-400 max-w-[480px] truncate" title={k.description}>{k.description}</div>}
                    </td>
                    <td className="text-[12px] text-ink-700">{k.unit}</td>
                    <td className="text-right font-mono tabular-nums text-ink-800">
                      {k.unitPriceNet != null ? `${formatMoney(k.unitPriceNet)} zł` : <span className="text-ink-300">—</span>}
                    </td>
                    <td className="text-[12px] text-ink-700">{k.cyclic ? 'tak' : '—'}</td>
                    <td className="font-mono text-[11px] text-ink-500">{k.sortOrder}</td>
                    <td>{k.active
                      ? <span className="pill pill-green">aktywny</span>
                      : <span className="pill pill-amber">wyłączony</span>}</td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => setEditing(k)} className="text-water-600 hover:text-water-800 p-1" title="Edytuj">
                        <Icon name="edit" className="w-3.5 h-3.5"/>
                      </button>
                      {k.active && k.code !== 'INNE' && (
                        <button onClick={() => onDeactivate(k)} className="text-red-500 hover:text-red-700 p-1 ml-1" title="Wyłącz">
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {(creating || editing) && (
        <KindEditModal
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

type KindForm = { name: string; unit: string; price: string; cyclic: boolean; description: string; sortOrder: number; active: boolean };

function KindEditModal({ initial, onClose, onSaved }: {
  initial: FieldWorkKindEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = initial != null;
  const [form, setForm] = useState<KindForm>({
    name: initial?.name ?? '',
    unit: initial?.unit ?? 'szt.',
    price: initial?.unitPriceNet != null ? String(initial.unitPriceNet).replace('.', ',') : '',
    cyclic: initial?.cyclic ?? false,
    description: initial?.description ?? '',
    sortOrder: initial?.sortOrder ?? 100,
    active: initial?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!form.name.trim()) { setErr('Nazwa jest wymagana'); return; }
    const priceText = form.price.replace(/\s/g, '').replace(',', '.');
    const price = priceText === '' ? null : Number(priceText);
    if (price != null && (!Number.isFinite(price) || price < 0)) { setErr('Cena musi być liczbą nieujemną'); return; }
    setBusy(true); setErr(null);
    try {
      const data = {
        name: form.name.trim(), unit: form.unit.trim() || 'szt.',
        unitPriceNet: price, clearPrice: price == null,
        cyclic: form.cyclic, description: form.description, sortOrder: form.sortOrder,
        ...(isEdit ? { active: form.active } : {}),
      };
      if (isEdit && initial) await fieldWorkKinds.update(initial.id, data);
      else await fieldWorkKinds.create(data);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: form, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[600px] w-full" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">{isEdit ? 'Edycja rodzaju pracy' : 'Nowy rodzaj pracy'}</h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>

        <div className="p-5 space-y-3">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}

          <Field label="Nazwa*">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                   placeholder="np. Diagnostyka studni" className="input" autoFocus/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Jednostka">
              <input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}
                     placeholder="szt., otwór, mb, doba" className="input"/>
            </Field>
            <Field label="Cena netto za jednostkę [zł]">
              <input value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}
                     placeholder="puste = bez ceny" inputMode="decimal" className="input font-mono text-right"/>
            </Field>
          </div>
          <Field label="Opis (opcjonalny)">
            <textarea value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      placeholder="Co obejmuje cena, np. dojazd, raport z inspekcji"
                      className="input min-h-[60px]" rows={3}/>
          </Field>
          <Field label="Kolejność sortowania">
            <input type="number" value={form.sortOrder}
                   onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })}
                   className="input font-mono w-32"/>
          </Field>
          <label className="flex items-center gap-2 text-[12px] text-ink-700 cursor-pointer">
            <input type="checkbox" checked={form.cyclic} onChange={e => setForm({ ...form, cyclic: e.target.checked })}/>
            Praca cykliczna — po zakończeniu oś czasu zlecenia przypomni o kolejnym terminie
          </label>
          {isEdit && initial?.code !== 'INNE' && (
            <label className="flex items-center gap-2 text-[12px] text-ink-700 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })}/>
              Aktywny (do wyboru w rejestrze prac i w kreatorze oferty)
            </label>
          )}
        </div>

        <div className="border-t border-ink-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz zmiany' : 'Dodaj rodzaj')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      {children}
    </div>
  );
}
